import type { VolumeBatchStop, VolumeFailureKind } from './volume-failure.js';
import type { VolumeContext } from './volume-context.js';
import type { VolumeSchedule } from './fakes-volume-schedule.js';
export interface QueryVolume {
  // Measurement availability is independent of the latest refresh attempt.
  status: 'missing' | 'complete';
  value: number | null;
  source: 'wordstat' | 'google_ads';
  match: 'exact' | 'close_variants';
  period: 'last_30_days' | 'monthly_average_12_months';
  phrase: string | null;
  regionName: string;
  device: 'desktop' | 'mobile' | 'all';
  capturedAt: string | null;
  stale: boolean;
  refresh: QueryVolumeRefresh | null;
  google?: GoogleVolumeDetails;
  wordstat?: { sharedQueries: string[] };
  weightUnavailableReason?: string;
  // Computed allocation for the whole active or historical scope; provider evidence stays intact.
  weightQueries?: string[];
}

export interface QueryVolumeRefresh {
  batchId: string;
  status: 'pending' | 'processing' | 'complete' | 'error' | 'paused';
  failureKind?: VolumeFailureKind;
  requestedAt: string;
  startedAt: string | null;
  error: string | null;
}

export interface VolumeCoverage {
  known: number;
  total: number;
  zero: number;
  stale: number;
}

export function volumeCoverage(volumes: Iterable<QueryVolume | undefined>): VolumeCoverage {
  const result = { known: 0, total: 0, zero: 0, stale: 0 };
  for (const volume of volumes) {
    result.total++;
    if (volume?.status !== 'complete' || volume.value === null) continue;
    result.known++;
    if (volume.value === 0) result.zero++;
    if (volume.stale) result.stale++;
  }
  return result;
}

export function volumeCoverageLabel(coverage: VolumeCoverage): string {
  return `Частотность известна для ${coverage.known} из ${coverage.total} запросов · нулевая: ${coverage.zero} · устарела: ${coverage.stale}`;
}

export const GOOGLE_VOLUME_LIMIT = 10_000;
export const GOOGLE_VOLUME_LANGUAGES = {
  all: 'Все языки',
  '1031': 'Русский',
  '1000': 'Английский',
} as const;
export interface GoogleVolumeTarget {
  geoTargetId: string;
  languageId: keyof typeof GOOGLE_VOLUME_LANGUAGES;
}
export interface GoogleVolumeDetails {
  text: string;
  closeVariants: string[];
  sharedQueries: string[];
  monthlySearchVolumes: Array<{ month: string; value: number | null }>;
  languageId: GoogleVolumeTarget['languageId'];
  requestId: string | null;
}

export function volumeWeight(volume?: QueryVolume): number {
  if (volume?.status !== 'complete' || volume.value === null || volume.weightUnavailableReason)
    return 0;
  if (volume.source === 'google_ads') {
    const count = (volume.weightQueries ?? volume.google?.sharedQueries)?.length ?? 0;
    return count ? volume.value / count : 0;
  }
  return volume.value / (volume.wordstat?.sharedQueries.length || 1);
}

export interface VolumeBatch {
  paused: number;
  stop: VolumeBatchStop | null;
  context: VolumeContext;
  onlyMissing: boolean;
  id: string;
  retryFailed: boolean;
  profileId: string | null;
  createdAt: string;
  total: number;
  complete: number;
  failed: number;
  pending: number;
}
export interface VolumeState {
  sources: Record<
    VolumeContext['source'],
    { configured: boolean; context: VolumeContext | null; schedule: VolumeSchedule }
  >;
  coverage: VolumeCoverage;
  coverageContext: VolumeContext | null;
  retryableFailed: number;
  schedule: VolumeSchedule;
  configured: boolean;
  supported: boolean;
  latest: VolumeBatch | null;
  googleTarget?: (GoogleVolumeTarget & { regionName: string }) | null;
}

export function volumeLabel(volume?: QueryVolume): string {
  const refresh = volume?.refresh;
  const suffix =
    refresh?.status === 'paused'
      ? ' · обновление приостановлено'
      : refresh?.status === 'error'
        ? ' · ошибка обновления'
        : refresh?.status === 'pending' || refresh?.status === 'processing'
          ? ' · обновляется…'
          : '';
  const source = volume?.source === 'google_ads' ? 'Google Ads' : 'Яндекс';
  if (!volume || volume.status === 'missing')
    return `Частотность не собрана${volume ? ` · ${source}` : ''}${suffix}`;
  const state = `${volume.stale ? ' · устарело' : ''}${suffix}${volume.weightUnavailableReason ? ' · вес не определён' : ''}`;
  if (volume.source === 'google_ads')
    return `≈ ${volume.value!.toLocaleString('ru-RU')} / мес. · ${source}${(volume.google?.sharedQueries.length ?? 0) > 1 ? ` · общий на ${volume.google!.sharedQueries.length}` : ''}${state}`;
  return `${volume.value!.toLocaleString('ru-RU')} · ${source}${(volume.wordstat?.sharedQueries.length ?? 0) > 1 ? ` · общий на ${volume.wordstat!.sharedQueries.length}` : ''}${state}`;
}

export function exactWordstatPhrase(query: string): string {
  const words = query.trim().split(/\s+/);
  if (words.some((word) => !/^[\p{L}\p{N}][\p{L}\p{N}._-]*$/u.test(word)))
    throw new Error(
      'Точная частотность требует обычных слов без поисковых операторов и спецсимволов',
    );
  const phrase = `"${words.map((word) => `!${word}`).join(' ')}"`;
  if (phrase.length > 400) throw new Error('Точная фраза Wordstat превышает 400 символов');
  return phrase;
}

// Quotes and ! fix the word count and forms, but Wordstat ignores word order.
// Preserve repeated words, spelling and forms; do not infer Google groups here.
export function canonicalWordstatPhrase(query: string): string {
  return exactWordstatPhrase(query.trim().toLowerCase().split(/\s+/).sort().join(' '));
}
export function wordstatKey(query: string): string | null {
  try {
    return canonicalWordstatPhrase(query);
  } catch {
    // Unsupported operators are not evidence that two queries share demand.
    return null;
  }
}
